/**
 * Unauthenticated login page for the tenant SPA.
 *
 * Split-screen layout inspired by the lovable mock at
 * `lovable-mock/src/pages/hyres/HyresLogin.tsx`:
 *   - left: brand panel (owner primary color, owner logo, hardcoded copy)
 *   - right: Clerk's built-in <SignIn /> component
 *
 * Owner-specific bits (logo, name, primary color) come from the
 * pre-mount bootstrap script loaded blocking in index.html (see
 * `src/owner-branding.ts` / REGTA-74), NOT from regta-backend. The
 * script is produced by `packages/auth-data/src/branding-export.ts` —
 * see that module for the security model (allow-listed public fields
 * only). Nothing about this page reaches the contract-bearing API
 * surface, and the branding is available synchronously, so the page
 * renders owner chrome on first paint.
 */

import { SignIn, useClerk } from '@clerk/clerk-react';
import { buildSyncRedirectUrl } from '@regta/common/clerk-satellite';
import { clerkProxyUrl, signInOnPrimary } from '../clerk-config.js';
import { ownerPublicBranding } from '../owner-branding.js';
import { usePageTitle } from '../use-page-title.js';

export function LoginPage() {
  usePageTitle('Logga in');
  const branding = ownerPublicBranding();
  const primary = branding.primaryColor;

  return (
    <div
      className="ledger flex min-h-screen bg-[var(--ledger-paper)]"
      style={
        {
          // Expose owner primary as a CSS var so the brand panel and
          // accents can stay declarative.
          ['--owner-primary' as never]: primary,
        } as React.CSSProperties
      }
    >
      {/* Left — brand panel. Hidden on small screens; shown on lg+. */}
      <div
        className="relative hidden items-center justify-center px-12 lg:flex lg:w-1/2"
        style={{ backgroundColor: 'var(--owner-primary)' }}
      >
        <div className="absolute top-8 left-12">
          <span
            className="ledger-meta"
            style={{ color: 'rgba(255,255,255,0.55)' }}
          >
            Hyresvärdens portal
          </span>
        </div>

        <div className="flex flex-col items-center text-center">
          {branding.logoOnPrimaryUrl ? (
            <img
              src={branding.logoOnPrimaryUrl}
              alt={branding.ownerName}
              className="max-h-32 w-auto"
            />
          ) : (
            <span
              className="ledger-display"
              style={{ color: '#ffffff', fontSize: '48px' }}
            >
              {branding.ownerName}
            </span>
          )}
          <p
            className="ledger-display mt-10 max-w-md text-sm italic"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            Avtal och förvaltning, samlat.
          </p>
        </div>

        <div
          className="absolute bottom-8 left-12 right-12 flex justify-between"
          style={{ color: 'rgba(255,255,255,0.45)' }}
        >
          <span className="ledger-meta" style={{ color: 'inherit' }}>
            Powered by Regta
          </span>
          <span className="ledger-meta" style={{ color: 'inherit' }}>
            {branding.ownerSlug}.regta.se
          </span>
        </div>
      </div>

      {/* Right — sign-in form */}
      <div className="flex w-full flex-col items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="w-full max-w-sm">
          {/* Small-screen brand mark (lg- hides the left panel). */}
          <div className="mb-10 flex items-center justify-center lg:hidden">
            {branding.logoOnWhiteUrl ? (
              <img
                src={branding.logoOnWhiteUrl}
                alt={branding.ownerName}
                className="max-h-16 w-auto"
              />
            ) : (
              <span
                className="ledger-display"
                style={{ fontSize: '28px', color: 'var(--ledger-ink)' }}
              >
                {branding.ownerName}
              </span>
            )}
          </div>

          <h1
            className="ledger-display"
            style={{ fontSize: '36px', lineHeight: 1.1, marginBottom: '8px' }}
          >
            Logga in
          </h1>
          <p className="ledger-meta mb-8">Hyresvärdens portal</p>

          {/*
            Static sign-in guidance. The copy is currently
            Hyresbostadsägarna-specific but rendered for every owner —
            per-owner configuration is intentionally out of scope for now.
          */}
          <div className="mb-8">
            <p
              className="ledger-display"
              style={{ fontSize: '18px', marginBottom: '8px' }}
            >
              Medlemsportal för Hyresbostadsägarna
            </p>
            <p className="ledger-meta" style={{ lineHeight: 1.6 }}>
              Du loggar in med den e-postadress du angav vid din
              medlemsansökan. Har du inte fått din inbjudan? Kontakta oss på{' '}
              <a
                href="mailto:medlem@hyresbostadsagarna.org"
                style={{
                  color: 'var(--owner-primary)',
                  textDecoration: 'underline',
                }}
              >
                medlem@hyresbostadsagarna.org
              </a>
              .
            </p>
          </div>

          <SignInSurface />

          <p className="ledger-meta mt-8" style={{ lineHeight: 1.6 }}>
            Inte medlem?{' '}
            <a
              href="https://medlem.hyresbostadsagarna.org"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--owner-primary)',
                textDecoration: 'underline',
              }}
            >
              Bli medlem här
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * The sign-in surface (ADR-0091).
 *
 * On a satellite build Clerk forbids rendering `<SignIn/>` here — sign-in must
 * complete on the primary domain — so we link out with `buildSignInUrl()`,
 * which appends the marker that syncs the session on the way back. The owner
 * branding around this stays: the customer was sold a branded portal, and only
 * the credential step moves.
 *
 * `signInForceRedirectUrl` targets this exact page, so the user returns to
 * the owner portal they started on rather than a shared landing page — but
 * routed through the proxy's `/v1/client/sync` first (`buildSyncRedirectUrl`),
 * not straight to the page. clerk-js is meant to sync the session itself on
 * the way back from the primary; that has not been firing reliably, and the
 * explicit sync hop is the workaround until Clerk support resolves why. The
 * primary must list our owner origins in `allowedRedirectOrigins` for the
 * final hop to be honoured.
 */
function SignInSurface() {
  const clerk = useClerk();

  if (!signInOnPrimary) {
    return (
      <SignIn
        routing="virtual"
        appearance={{
          elements: {
            // The owner brand panel + the "Logga in" heading already tell the
            // user where they are — Clerk's default "Sign in to <app>" header
            // would be redundant.
            headerTitle: 'hidden',
            headerSubtitle: 'hidden',
          },
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className="w-full rounded-md px-5 py-3 text-sm font-medium text-white"
      style={{ background: 'var(--owner-primary)' }}
      onClick={() => {
        window.location.href = clerk.buildSignInUrl({
          signInForceRedirectUrl: clerkProxyUrl
            ? buildSyncRedirectUrl(clerkProxyUrl, window.location.href)
            : window.location.href,
        });
      }}
    >
      Logga in
    </button>
  );
}
